/**
 * Futures Contract Specifications
 *
 * Contains tick size, tick value, and point value for each contract.
 * Used for P&L calculations, position sizing, MAE/MFE tick conversion,
 * ATM bracket interpretation.
 *
 * Symbol normalization
 * --------------------
 * Keys here are ROOT symbols only (e.g., 'MNQ', not 'MNQ 06-26' or 'MNQM6').
 * Callers MUST pass a rooted symbol — use `baseSymbol(s)` at the lookup
 * site if the source might include a contract month suffix. `getSpec()`
 * applies `baseSymbol()` automatically so most callers don't need to
 * worry about it.
 *
 * Last Updated: May 2026
 */

export const FUTURES_SPECS = {
  // ===========================================
  // MICRO EQUITY INDEX
  // ===========================================
  'MNQ': { tickSize: 0.25, tickValue: 0.50, pointValue: 2 },
  'MES': { tickSize: 0.25, tickValue: 1.25, pointValue: 5 },
  'M2K': { tickSize: 0.10, tickValue: 0.50, pointValue: 5 },
  'MYM': { tickSize: 1.00, tickValue: 0.50, pointValue: 0.50 },

  // ===========================================
  // STANDARD EQUITY INDEX
  // ===========================================
  'NQ': { tickSize: 0.25, tickValue: 5.00, pointValue: 20 },
  'ES': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  'RTY': { tickSize: 0.10, tickValue: 5.00, pointValue: 50 },
  'YM': { tickSize: 1.00, tickValue: 5.00, pointValue: 5 },
  'EMD': { tickSize: 0.10, tickValue: 10.00, pointValue: 100 },
  'NKD': { tickSize: 5.00, tickValue: 25.00, pointValue: 5 },

  // ===========================================
  // VOLATILITY (VIX FAMILY)
  // ===========================================
  'VX':  { tickSize: 0.05, tickValue: 50.00, pointValue: 1000 },  // Standard VIX
  'VXM': { tickSize: 0.05, tickValue: 5.00,  pointValue: 100  },  // Micro VIX
  
  // ===========================================
  // MICRO ENERGY
  // ===========================================
  'MCL': { tickSize: 0.01, tickValue: 1.00, pointValue: 100 },
  'MHO': { tickSize: 0.0001, tickValue: 0.42, pointValue: 4200 },
  'MRB': { tickSize: 0.0001, tickValue: 0.42, pointValue: 4200 },
  'MNG': { tickSize: 0.005, tickValue: 1.25, pointValue: 2500 },  // Micro Henry Hub Nat Gas

  // ===========================================
  // STANDARD ENERGY
  // ===========================================
  'CL': { tickSize: 0.01, tickValue: 10.00, pointValue: 1000 },
  'QM': { tickSize: 0.025, tickValue: 12.50, pointValue: 500 },
  'NG': { tickSize: 0.001, tickValue: 10.00, pointValue: 10000 },
  'QG': { tickSize: 0.005, tickValue: 12.50, pointValue: 2500 },
  'HO': { tickSize: 0.0001, tickValue: 4.20, pointValue: 42000 },
  'RB': { tickSize: 0.0001, tickValue: 4.20, pointValue: 42000 },
  'BZ': { tickSize: 0.01, tickValue: 10.00, pointValue: 1000 },   // ICE Brent crude
  
  // ===========================================
  // MICRO METALS
  // ===========================================
  'MGC': { tickSize: 0.10, tickValue: 1.00, pointValue: 10 },
  'SIL': { tickSize: 0.005, tickValue: 2.50, pointValue: 500 },
  
  // ===========================================
  // STANDARD METALS
  // ===========================================
  'GC': { tickSize: 0.10, tickValue: 10.00, pointValue: 100 },
  'SI': { tickSize: 0.005, tickValue: 25.00, pointValue: 5000 },
  'HG': { tickSize: 0.0005, tickValue: 12.50, pointValue: 25000 },
  'PL': { tickSize: 0.10, tickValue: 5.00, pointValue: 50 },
  'PA': { tickSize: 0.10, tickValue: 10.00, pointValue: 100 },
  'QI': { tickSize: 0.0125, tickValue: 12.50, pointValue: 1000 },
  'QO': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  
  // ===========================================
  // MICRO FX
  // ===========================================
  'M6E': { tickSize: 0.0001, tickValue: 1.25, pointValue: 12500 },
  'M6A': { tickSize: 0.0001, tickValue: 1.00, pointValue: 10000 },
  'M6B': { tickSize: 0.0001, tickValue: 0.625, pointValue: 6250 },
  'M6J': { tickSize: 0.000001, tickValue: 1.25, pointValue: 1250000 },
  'MCD': { tickSize: 0.0001, tickValue: 1.00, pointValue: 10000 },
  'MISF': { tickSize: 0.0001, tickValue: 1.25, pointValue: 12500 },
  
  // ===========================================
  // STANDARD FX
  // ===========================================
  '6E': { tickSize: 0.00005, tickValue: 6.25, pointValue: 125000 },
  '6A': { tickSize: 0.0001, tickValue: 10.00, pointValue: 100000 },
  '6B': { tickSize: 0.0001, tickValue: 6.25, pointValue: 62500 },
  '6J': { tickSize: 0.0000005, tickValue: 6.25, pointValue: 12500000 },
  '6C': { tickSize: 0.00005, tickValue: 5.00, pointValue: 100000 },
  '6S': { tickSize: 0.0001, tickValue: 12.50, pointValue: 125000 },
  '6N': { tickSize: 0.0001, tickValue: 10.00, pointValue: 100000 },
  '6M': { tickSize: 0.00001, tickValue: 5.00, pointValue: 500000 },
  'E7': { tickSize: 0.0001, tickValue: 6.25, pointValue: 62500 },
  'J7': { tickSize: 0.000001, tickValue: 6.25, pointValue: 6250000 },
  
  // ===========================================
  // CRYPTO
  // ===========================================
  'MBT': { tickSize: 5.00, tickValue: 0.50, pointValue: 0.10 },
  'MET': { tickSize: 0.10, tickValue: 0.50, pointValue: 5 },
  'BTC': { tickSize: 5.00, tickValue: 25.00, pointValue: 5 },
  'ETH': { tickSize: 0.50, tickValue: 25.00, pointValue: 50 },     // Standard Ether
  'MSL': { tickSize: 0.01, tickValue: 0.25, pointValue: 25 },      // Micro Solana
  'MAV': { tickSize: 0.005, tickValue: 0.25, pointValue: 50 },     // Micro Avalanche

  // ===========================================
  // SOFT COMMODITIES (ICE US)
  // ===========================================
  'CC': { tickSize: 1.00,   tickValue: 10.00, pointValue: 10 },    // Cocoa
  'KC': { tickSize: 0.05,   tickValue: 18.75, pointValue: 375 },   // Coffee 'C'
  'CT': { tickSize: 0.01,   tickValue: 5.00,  pointValue: 500 },   // Cotton #2
  'SB': { tickSize: 0.01,   tickValue: 11.20, pointValue: 1120 },  // Sugar #11
  'OJ': { tickSize: 0.05,   tickValue: 7.50,  pointValue: 150 },   // Orange Juice
  'RM': { tickSize: 1.00,   tickValue: 10.00, pointValue: 10 },    // Robusta Coffee (LIFFE)
  
  // ===========================================
  // AGRICULTURE
  // ===========================================
  'ZC': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  'ZS': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  'ZW': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  'ZM': { tickSize: 0.10, tickValue: 10.00, pointValue: 100 },
  'ZL': { tickSize: 0.01, tickValue: 6.00, pointValue: 600 },
  'ZR': { tickSize: 0.005, tickValue: 10.00, pointValue: 2000 },
  'ZO': { tickSize: 0.25, tickValue: 12.50, pointValue: 50 },
  'HE': { tickSize: 0.00025, tickValue: 10.00, pointValue: 40000 },
  'LE': { tickSize: 0.00025, tickValue: 10.00, pointValue: 40000 },
  'GF': { tickSize: 0.00025, tickValue: 12.50, pointValue: 50000 },
  'LB': { tickSize: 0.10, tickValue: 11.00, pointValue: 110 },
  'XW': { tickSize: 0.125, tickValue: 1.25, pointValue: 10 },
  'XC': { tickSize: 0.125, tickValue: 1.25, pointValue: 10 },
  'XK': { tickSize: 0.125, tickValue: 1.25, pointValue: 10 },
  
  // ===========================================
  // INTEREST RATES / TREASURIES
  // ===========================================
  'ZN': { tickSize: 0.015625, tickValue: 15.625, pointValue: 1000 },
  'ZB': { tickSize: 0.03125, tickValue: 31.25, pointValue: 1000 },
  'ZF': { tickSize: 0.0078125, tickValue: 7.8125, pointValue: 1000 },
  'ZT': { tickSize: 0.0078125, tickValue: 15.625, pointValue: 2000 },
  'ZQ': { tickSize: 0.005, tickValue: 20.835, pointValue: 4167 },
  'UB': { tickSize: 0.03125, tickValue: 31.25, pointValue: 1000 },
  '10Y': { tickSize: 0.001, tickValue: 1.00, pointValue: 1000 },     // Micro 10Y Yield
  '2YY': { tickSize: 0.001, tickValue: 1.00, pointValue: 1000 },     // Micro 2Y Yield
  '30Y': { tickSize: 0.001, tickValue: 1.00, pointValue: 1000 },     // Micro 30Y Yield
  '5YY': { tickSize: 0.001, tickValue: 1.00, pointValue: 1000 },     // Micro 5Y Yield
  'SR3': { tickSize: 0.0025, tickValue: 6.25,  pointValue: 2500 },   // 3-Month SOFR
  'SR1': { tickSize: 0.0025, tickValue: 10.4175, pointValue: 4167 }, // 1-Month SOFR (matches ZQ method)
  
  // ===========================================
  // EUREX (European)
  // ===========================================
  'FDAX': { tickSize: 0.50, tickValue: 12.50, pointValue: 25 },
  'FDXM': { tickSize: 1.00, tickValue: 5.00, pointValue: 5 },
  'FDXS': { tickSize: 1.00, tickValue: 1.00, pointValue: 1 },
  'FESX': { tickSize: 1.00, tickValue: 10.00, pointValue: 10 },
  'FSXE': { tickSize: 1.00, tickValue: 1.00, pointValue: 1 },
  'FVS': { tickSize: 0.05, tickValue: 50.00, pointValue: 1000 },
  'FXXP': { tickSize: 0.10, tickValue: 5.00, pointValue: 50 },
  'FGBL': { tickSize: 0.01, tickValue: 10.00, pointValue: 1000 },
  'FGBM': { tickSize: 0.01, tickValue: 10.00, pointValue: 1000 },
  'FGBS': { tickSize: 0.005, tickValue: 5.00, pointValue: 1000 },
  'FGBX': { tickSize: 0.02, tickValue: 20.00, pointValue: 1000 },
};

/**
 * Round a price difference to the nearest tick count.
 * Prevents floating-point errors like 0.0043 / 0.0001 = 42.9999999.
 * @param {number} priceDiff - Price difference (entry-exit or exit-entry)
 * @param {number} tickSize - Instrument tick size
 * @returns {number} - Integer tick count (rounded)
 */
export const safeTicks = (priceDiff, tickSize) => {
  return Math.round(priceDiff / tickSize);
};

/**
 * Snap a price to the nearest valid tick for an instrument.
 * Useful for weighted-average entry prices that may drift off-tick.
 * @param {number} price - Raw price (e.g. weighted average)
 * @param {number} tickSize - Instrument tick size
 * @returns {number} - Price snapped to nearest tick
 */
export const snapToTick = (price, tickSize) => {
  return Math.round(price / tickSize) * tickSize;
};

/**
 * Strip a futures contract month suffix from a symbol, returning the bare
 * ticker root. Handles all three formats the codebase encounters:
 *
 *   'MNQ'         → 'MNQ'          (already root)
 *   'MNQ 06-26'   → 'MNQ'          (NinjaTrader full name, space-delimited)
 *   'MNQM6'       → 'MNQ'          (NT short contract code: root + month + year-digit)
 *   'MNQH26'      → 'MNQ'          (NT short code, 2-digit year)
 *   '6E'          → '6E'           (numeric-prefix FX root)
 *   '6E 06-26'    → '6E'           (numeric-prefix FX with month)
 *   '6EM6'        → '6E'           (numeric-prefix FX short code)
 *   ''            → ''
 *   null/undefined → ''
 *
 * Why this exists
 * ---------------
 * 9 historical sites in the codebase did `futuresSpecs[trade.symbol]` and
 * assumed the symbol was already root-stripped. That assumption only held
 * because `tradeProcessor.getSymbol` happened to strip month codes at write
 * time. As soon as ANY write path preserves the contract month (manual
 * entry, a new import format, JournifiSync configured to use full names),
 * those lookups silently return `undefined` and downstream P&L math goes
 * to NaN. Centralizing the strip in this helper means new callers can do
 * `getSpec(trade.symbol)` and not have to think about it.
 *
 * @param {string} sym - Symbol in any of the recognized forms above
 * @returns {string} - Uppercase root symbol, or '' if input is falsy
 */
export const baseSymbol = (sym) => {
  if (!sym || typeof sym !== 'string') return '';
  const trimmed = sym.trim().toUpperCase();
  if (!trimmed) return '';
  // Form 1: "MNQ 06-26" — space + MM-YY suffix
  const spaceMatch = trimmed.match(/^([A-Z0-9]+)\s+\d{2}-\d{2}$/);
  if (spaceMatch) return spaceMatch[1];
  // Form 2: "MNQM6" or "MNQH26" — root + month code (FGHJKMNQUVXZ) + 1-2 digit year
  // Anchor to require the root to be at least 1 char and not start with a digit
  // (covers cases like 'NQU5'); we intentionally accept numeric-prefix roots
  // like '6E' by allowing them through the third branch.
  const codeMatch = trimmed.match(/^([A-Z][A-Z0-9]*?)([FGHJKMNQUVXZ])(\d{1,2})$/);
  if (codeMatch) {
    // Disambiguate: '6E' would match as root='6', code='E', year=... — wrong.
    // Re-check by requiring the root to NOT be a single character that's also
    // a valid month code. If ambiguous, fall through to "already root".
    const candidate = codeMatch[1];
    if (candidate.length >= 2 || /^[A-Z]/.test(candidate)) return candidate;
  }
  // Form 3: "6E 06-26" — numeric-prefix FX with month
  const numSpaceMatch = trimmed.match(/^(\d[A-Z]+)\s+\d{2}-\d{2}$/);
  if (numSpaceMatch) return numSpaceMatch[1];
  // Form 4: "6EM6" — numeric-prefix FX short code
  const numCodeMatch = trimmed.match(/^(\d[A-Z]+?)([FGHJKMNQUVXZ])(\d{1,2})$/);
  if (numCodeMatch) return numCodeMatch[1];
  // Already a bare root (or unrecognized — return as-is for caller's fallback)
  return trimmed;
};

/**
 * Extract the contract month from a symbol, if present.
 *
 *   'MNQ 06-26'   → '06-26'
 *   'MNQM6'       → 'M6'   (NT short code: month letter + year digit)
 *   'MNQH26'      → 'H26'  (with 2-digit year)
 *   'MNQ'         → ''     (no month info)
 *
 * Used by trade write paths that want to preserve contract-month context
 * for per-contract analytics WITHOUT changing the canonical `symbol` field.
 *
 * @param {string} sym
 * @returns {string} - Contract month in original form, or '' if none present
 */
export const extractContractMonth = (sym) => {
  if (!sym || typeof sym !== 'string') return '';
  const trimmed = sym.trim().toUpperCase();
  if (!trimmed) return '';
  const spaceMatch = trimmed.match(/^[A-Z0-9]+\s+(\d{2}-\d{2})$/);
  if (spaceMatch) return spaceMatch[1];
  const codeMatch = trimmed.match(/^[A-Z][A-Z0-9]*?([FGHJKMNQUVXZ]\d{1,2})$/);
  if (codeMatch) return codeMatch[1];
  return '';
};

/**
 * Get spec for a symbol. Accepts ANY of the recognized symbol forms —
 * applies `baseSymbol()` internally so callers don't have to.
 *
 * @param {string} symbol - Trading symbol in any recognized form
 * @returns {object|null} - {tickSize, tickValue, pointValue} or null
 */
export const getSpec = (symbol) => {
  if (!symbol) return null;
  const root = baseSymbol(symbol);
  if (!root) return null;
  return FUTURES_SPECS[root] || null;
};

/**
 * Calculate P&L for a trade
 * @param {string} symbol - Trading symbol
 * @param {number} entry - Entry price
 * @param {number} exit - Exit price
 * @param {number} quantity - Number of contracts
 * @param {string} direction - 'long' or 'short'
 * @returns {number} - P&L in dollars
 */
export const calculatePnL = (symbol, entry, exit, quantity, direction = 'long') => {
  const spec = getSpec(symbol);
  if (!spec) return 0;
  
  const isShort = direction.toLowerCase() === 'short';
  const multiplier = isShort ? -1 : 1;
  const points = (exit - entry) * multiplier;
  const ticks = safeTicks(points, spec.tickSize);
  return ticks * spec.tickValue * quantity;
};

/**
 * Calculate risk in dollars for a stop loss
 * @param {string} symbol - Trading symbol
 * @param {number} stopLossPoints - Stop loss distance in points
 * @param {number} quantity - Number of contracts
 * @returns {number} - Risk in dollars
 */
export const calculateRisk = (symbol, stopLossPoints, quantity) => {
  const spec = getSpec(symbol);
  if (!spec) return 0;

  const ticks = safeTicks(stopLossPoints, spec.tickSize);
  return ticks * spec.tickValue * quantity;
};

/**
 * Get all available symbols
 * @returns {string[]} - Array of symbol names
 */
export const getAllSymbols = () => {
  return Object.keys(FUTURES_SPECS);
};

/**
 * Group symbols by category
 * @returns {object} - {category: [symbols]}
 */
export const getSymbolsByCategory = () => {
  return {
    'Micro Equity Index': ['MNQ', 'MES', 'M2K', 'MYM'],
    'Standard Equity Index': ['NQ', 'ES', 'RTY', 'YM', 'EMD', 'NKD'],
    'Volatility': ['VX', 'VXM'],
    'Micro Energy': ['MCL', 'MHO', 'MRB', 'MNG'],
    'Standard Energy': ['CL', 'QM', 'NG', 'QG', 'HO', 'RB', 'BZ'],
    'Micro Metals': ['MGC', 'SIL'],
    'Standard Metals': ['GC', 'SI', 'HG', 'PL', 'PA', 'QI', 'QO'],
    'Micro FX': ['M6E', 'M6A', 'M6B', 'M6J', 'MCD', 'MISF'],
    'Standard FX': ['6E', '6A', '6B', '6J', '6C', '6S', '6N', '6M', 'E7', 'J7'],
    'Crypto': ['MBT', 'MET', 'BTC', 'ETH', 'MSL', 'MAV'],
    'Soft Commodities': ['CC', 'KC', 'CT', 'SB', 'OJ', 'RM'],
    'Agriculture': ['ZC', 'ZS', 'ZW', 'ZM', 'ZL', 'ZR', 'ZO', 'HE', 'LE', 'GF', 'LB', 'XW', 'XC', 'XK'],
    'Interest Rates': ['ZN', 'ZB', 'ZF', 'ZT', 'ZQ', 'UB', '10Y', '2YY', '30Y', '5YY', 'SR3', 'SR1'],
    'Eurex': ['FDAX', 'FDXM', 'FDXS', 'FESX', 'FSXE', 'FVS', 'FXXP', 'FGBL', 'FGBM', 'FGBS', 'FGBX'],
  };
};

export default FUTURES_SPECS;

// Alias for backward compatibility with utils/futuresSpecs imports
export const futuresSpecs = FUTURES_SPECS;
